import React, { useEffect, useMemo, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';

function authHeaders(): Record<string, string> {
  const t = localStorage.getItem('intrlock_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function apiGet<T>(path: string): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json();
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    throw new Error(text || `${r.status} ${r.statusText}`);
  }
  return r.json();
}

/**
 * admin.intrlock.io → System → Send Push
 *
 * Compose a one-off APNs alert push to a tenant, a property's tenants,
 * or a customer's entire portfolio. Backed by POST /admin/push/send on
 * intrlock-org-api-v2.
 */

type TargetKind = 'person' | 'property' | 'customer';

interface Property {
  id: string;
  name?: string;
  property_name?: string;
  customer_id?: string;
}

interface Person {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  property_id?: string;
  unit_number?: string;
  device_count?: number;
}

interface Customer {
  id: string;
  name?: string;
  customer_name?: string;
}

interface DeliveryRow {
  target_id: string;
  delivered: number;
  recipients?: number;
  skipped?: number;
}

const EVENT_TYPES: Array<{ value: string; label: string }> = [
  { value: 'announcement', label: 'Building announcement' },
  { value: 'door_unlocked', label: 'Door unlocked alert' },
  { value: 'delivery_arrived', label: 'Delivery arrived' },
  { value: 'guest_arrived', label: 'Guest arrived' },
  { value: 'camera_offline', label: 'Camera offline' },
  { value: 'camera_online', label: 'Camera back online' },
];

export default function PushSender() {
  const [targetKind, setTargetKind] = useState<TargetKind>('property');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [eventType, setEventType] = useState('announcement');

  const [properties, setProperties] = useState<Property[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [filterPropertyId, setFilterPropertyId] = useState<string>('');
  const [loadingTargets, setLoadingTargets] = useState(false);

  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ delivered: number; targets: DeliveryRow[] } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingTargets(true);
      try {
        // org-api returns raw arrays from /properties and /customers.
        const [props, custs] = await Promise.all([
          apiGet<Property[] | { properties?: Property[] }>('/properties').catch(() => [] as Property[]),
          apiGet<Customer[] | { customers?: Customer[] }>('/customers').catch(() => [] as Customer[]),
        ]);
        if (cancelled) return;
        const p: Property[] = Array.isArray(props)
          ? props
          : Array.isArray((props as any).properties) ? (props as any).properties : [];
        const c: Customer[] = Array.isArray(custs)
          ? custs
          : Array.isArray((custs as any).customers) ? (custs as any).customers : [];
        setProperties(p);
        setCustomers(c);
      } finally {
        if (!cancelled) setLoadingTargets(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // When the user switches to "person" mode, default the property filter
    // to the first property so the list isn't empty.
    if (targetKind === 'person' && !filterPropertyId && properties[0]) {
      setFilterPropertyId(properties[0].id);
    }
  }, [targetKind, properties, filterPropertyId]);

  useEffect(() => {
    if (targetKind !== 'person' || !filterPropertyId) {
      setPeople([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Only show people who have actually activated the iOS app so admins
        // never pick a recipient whose push would be silently dropped at
        // delivery. The dedicated admin endpoint returns just those.
        const r = await apiGet<Person[]>(`/admin/push/recipients?property_id=${encodeURIComponent(filterPropertyId)}`);
        if (cancelled) return;
        setPeople(Array.isArray(r) ? r : []);
      } catch {
        if (!cancelled) setPeople([]);
      }
    })();
    return () => { cancelled = true; };
  }, [targetKind, filterPropertyId]);

  // Reset selection when target kind changes so we don't accidentally
  // send to a stale list.
  useEffect(() => { setSelectedIds([]); setResult(null); setErrorMsg(null); }, [targetKind]);

  const targetList: Array<{ id: string; label: string }> = useMemo(() => {
    if (targetKind === 'property') {
      return properties.map((p) => ({
        id: p.id,
        label: p.property_name ?? p.name ?? p.id,
      }));
    }
    if (targetKind === 'customer') {
      return customers.map((c) => ({
        id: c.id,
        label: c.customer_name ?? c.name ?? c.id,
      }));
    }
    return people.map((p) => {
      const name = `${(p.first_name ?? '').trim()} ${(p.last_name ?? '').trim()}`.trim()
        || p.email
        || p.id;
      const unit = p.unit_number ? ` · Unit ${p.unit_number}` : '';
      const devices = (p.device_count ?? 0) > 1 ? ` · ${p.device_count} devices` : '';
      return { id: p.id, label: `${name}${unit}${devices}` };
    });
  }, [targetKind, properties, customers, people]);

  const toggleId = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const selectAll = () => setSelectedIds(targetList.map((t) => t.id));
  const clearSelection = () => setSelectedIds([]);

  const send = async () => {
    setErrorMsg(null);
    setResult(null);
    if (!title.trim() || !body.trim()) {
      setErrorMsg('Title and message are required.');
      return;
    }
    if (selectedIds.length === 0) {
      setErrorMsg('Pick at least one target.');
      return;
    }
    setSending(true);
    try {
      const resp = await apiPost<{ delivered: number; targets: DeliveryRow[] }>(
        '/admin/push/send',
        {
          target: { type: targetKind, ids: selectedIds },
          title: title.trim(),
          body: body.trim(),
          event_type: eventType,
        },
      );
      setResult(resp);
    } catch (err: any) {
      setErrorMsg(err?.message ?? 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ maxWidth: 980, padding: '24px', margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px' }}>Send Push Notification</h1>
      <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 24px' }}>
        Composer for ad-hoc alerts to tenants' iPhones. Respects each user's
        per-event notification preferences — disabled categories are silently
        skipped at delivery.
      </p>

      <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        {/* Target kind */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 8 }}>
            Send to
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['property', 'customer', 'person'] as TargetKind[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setTargetKind(k)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: '1px solid ' + (targetKind === k ? '#0f172a' : '#e2e8f0'),
                  background: targetKind === k ? '#0f172a' : '#fff',
                  color: targetKind === k ? '#fff' : '#334155',
                  fontSize: 13, fontWeight: 500, cursor: 'pointer',
                }}
              >
                {k === 'property' ? 'Property' : k === 'customer' ? 'Customer' : 'Specific People'}
              </button>
            ))}
          </div>
        </div>

        {/* Property filter for "person" mode */}
        {targetKind === 'person' && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>
              At property
            </label>
            <select
              value={filterPropertyId}
              onChange={(e) => setFilterPropertyId(e.target.value)}
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, minWidth: 260 }}
            >
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.property_name ?? p.name ?? p.id}</option>
              ))}
            </select>
          </div>
        )}

        {/* Target list */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
              Recipients ({selectedIds.length} selected)
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={selectAll}
                style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}>
                Select all
              </button>
              <button type="button" onClick={clearSelection}
                style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}>
                Clear
              </button>
            </div>
          </div>
          <div style={{
            maxHeight: 240, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, padding: 8,
          }}>
            {loadingTargets ? (
              <div style={{ color: '#64748b', fontSize: 13, padding: 12 }}>Loading…</div>
            ) : targetList.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: 13, padding: 12 }}>
                {targetKind === 'person'
                  ? 'No one at this property has activated the mobile app yet, so push notifications can\'t be delivered.'
                  : 'No options available.'}
              </div>
            ) : targetList.map((t) => (
              <label key={t.id} style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', borderRadius: 6, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(t.id)}
                  onChange={() => toggleId(t.id)}
                  style={{ marginRight: 10 }}
                />
                <span style={{ fontSize: 13, color: '#0f172a' }}>{t.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Message */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>
            Title
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Maintenance Tomorrow"
            maxLength={80}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>
            Message
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="The water in your building will be shut off from 9am–11am for routine repairs."
            maxLength={500}
            rows={4}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, resize: 'vertical' }}
          />
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>
            Category
          </label>
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
          >
            {EVENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4 }}>
            Users who disabled this category in their iOS app won't receive the push.
          </div>
        </div>

        {errorMsg && (
          <div style={{ background: '#fef2f2', color: '#b91c1c', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 12 }}>
            {errorMsg}
          </div>
        )}

        <button
          type="button"
          onClick={send}
          disabled={sending}
          style={{
            background: sending ? '#94a3b8' : '#0f172a',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '10px 18px',
            fontSize: 14,
            fontWeight: 600,
            cursor: sending ? 'not-allowed' : 'pointer',
          }}
        >
          {sending ? 'Sending…' : `Send to ${selectedIds.length} target${selectedIds.length === 1 ? '' : 's'}`}
        </button>

        {result && (
          <div style={{ marginTop: 20, background: '#f0fdf4', borderRadius: 8, padding: 14, border: '1px solid #bbf7d0' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#15803d', marginBottom: 6 }}>
              Delivered {result.delivered} push{result.delivered === 1 ? '' : 'es'}.
            </div>
            <div style={{ fontSize: 12, color: '#166534' }}>
              {result.targets.map((t) => (
                <div key={t.target_id}>
                  <code>{t.target_id.slice(0, 8)}…</code>: {t.delivered} delivered
                  {typeof t.recipients === 'number' ? `, ${t.recipients} recipient${t.recipients === 1 ? '' : 's'}` : ''}
                  {typeof t.skipped === 'number' ? `, ${t.skipped} skipped` : ''}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
